import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PlusIcon, UploadIcon, UsersRoundIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable';
import { DataTable } from '@/components/ui/table/data-table';
import { useServerPagination } from '@/components/ui/table/useServerPagination';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import type { Contact, ContactsResponse } from '@/shared/types/contacts';
import { actions } from '@/shared/types/permissions';
import { PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';

import { contactColumns } from './contactColumns';
import { ContactFormPanel } from '../components/ContactFormPanel';
import { EmptyContacts } from '../components/EmptyContacts';
import { contactSearchTerm } from '../constants';
import { useContacts } from '../hooks/useContacts';

const SEARCH_DEBOUNCE_MS = 300;

const ContactsToolbar = ({ onSearchChange }: { onSearchChange: (value: string) => void }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      onSearchChange(contactSearchTerm(value) ?? '');
    }, SEARCH_DEBOUNCE_MS);
  };

  return (
    <div className="mb-4 pl-1">
      <SearchBar
        placeholder={t('contacts.search_placeholder')}
        value={searchValue}
        onChange={handleSearchChange}
      />
    </div>
  );
};

const PAGE_SIZE = 25;

const selectContacts = (page: ContactsResponse) => page?.contacts ?? [];

export const ContactsListPage = () => {
  useSegments(PageName.CONTACTS);

  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const [search, setSearch] = useState('');
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'contacts', actions.WRITE)
  );
  const [isAddOpen, setIsAddOpen] = useState(false);

  useTitle(t('pages.contacts'));

  const { pages, total, status, fetchNextPage, hasNextPage, isFetchingNextPage } = useContacts({
    limit: PAGE_SIZE,
    sort: '-created',
    q: search || undefined,
  });

  const {
    currentPageIndex,
    setCurrentPageIndex,
    goToNextPage,
    hasNextPage: paginationHasNextPage,
    currentPageData,
  } = useServerPagination<ContactsResponse, Contact>(pages, fetchNextPage, {
    dataSelector: selectContacts,
    resetDependencies: [search],
    externalIsFetching: isFetchingNextPage,
    serverHasNextPage: hasNextPage,
  });

  const columns = useMemo(() => contactColumns(), []);

  const isLoading = status === 'pending';
  const isInitialLoad = isLoading && pages.length === 0;
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'contacts',
    isLoading: isInitialLoad,
    isEmpty: !search && total === 0,
    isError: status === 'error',
  });

  const getRowHref = useCallback(
    (contact: Contact) => `/${slug}/contacts/${contact.contact_id}`,
    [slug]
  );

  return (
    <ResizablePanelGroup
      direction="horizontal"
      className="h-full w-full"
      autoSaveId="contacts-sidebar"
    >
      <ResizablePanel order={1} className="flex min-h-0 flex-col overflow-hidden">
        <PageSubHeader
          breadcrumbs={
            <PageSubHeaderBreadcrumbs>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <div className="flex h-8 w-8 items-center justify-center">
                        <UsersRoundIcon size={16} className="text-foreground-muted" />
                      </div>
                      <div className="mr-4 ml-2 h-4 w-px bg-border" />
                      <span className="text-sm leading-5 font-normal text-foreground">
                        {t('contacts.title')}
                      </span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </PageSubHeaderBreadcrumbs>
          }
          actions={
            !isEmptyPage && (
              <PageSubHeaderActions>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="sm" disabled={!canWrite}>
                      <PlusIcon size={16} />
                      {t('common.add')}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onSelect={() => setIsAddOpen(true)}>
                      <PlusIcon size={16} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{t('contacts.create')}</span>
                        <span className="text-xs text-foreground-muted">
                          {t('contacts.create_description')}
                        </span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem disabled>
                      <UploadIcon size={16} />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {t('contacts.upload')} · {t('common.coming_soon')}
                        </span>
                        <span className="text-xs text-foreground-muted">
                          {t('contacts.upload_description')}
                        </span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </PageSubHeaderActions>
            )
          }
        />
        {!canWrite && <ReadOnlyBanner />}

        <div className="min-h-0 flex-1 overflow-y-auto bg-bg-muted-light">
          <div className={TABLE_LAYOUT_FILL_CLASSES}>
            {showEmpty && <EmptyContacts canWrite={canWrite} onCreate={() => setIsAddOpen(true)} />}

            {showEmptySkeleton && <EmptySkeleton descriptionLines={3} />}

            {showList && (
              <>
                {/* Search bar */}
                <ContactsToolbar onSearchChange={setSearch} />

                {/* Table */}
                <DataTable.Root<Contact, unknown>
                  fill
                  className="min-h-0 flex-1"
                  columns={columns}
                  data={currentPageData}
                  pagination
                  currentPageIndex={currentPageIndex}
                  setCurrentPageIndex={setCurrentPageIndex}
                  fetchNextPage={goToNextPage}
                  hasNextPage={paginationHasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  rowCount={total}
                  resourceName="contact"
                  emptyStateMessage={t('contacts.no_contacts_found')}
                  isLoading={isLoading}
                  enableRowHover
                  getRowHref={getRowHref}
                >
                  <DataTable.Content />
                  <DataTable.Pagination />
                </DataTable.Root>
              </>
            )}
          </div>
        </div>
      </ResizablePanel>

      {isAddOpen && (
        <>
          <ResizableHandle />
          <ResizablePanel
            order={2}
            defaultSize={28}
            minSize={24}
            maxSize={40}
            className="bg-background"
          >
            <ContactFormPanel mode="create" onClose={() => setIsAddOpen(false)} />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  );
};
